import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { SYBERKEY_KEY_IMG, SYBERKEY_WORDMARK_IMG } from '../branding';
import SyberKeyInlineMark from '../components/SyberKeyInlineMark';
import './HomePage.css';

const HomePage: React.FC = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();

  const handleLoginClick = () => {
    navigate('/login');
  };

  const handleDashboardClick = () => {
    navigate('/dashboard');
  };

  return (
    <div className="home-container">
      {/* Navbar */}
      <nav className="navbar">
        <div className="container">
          <a className="navbar-brand" href="/">
            <img src={SYBERKEY_KEY_IMG} alt="" className="logo-img" />
            <img src={SYBERKEY_WORDMARK_IMG} alt="SyberKey" className="wordmark-img" />
          </a>

          <div className="navbar-nav">
            {isAuthenticated ? (
              <button className="btn-yellow" onClick={handleDashboardClick}>
                Dashboard
              </button>
            ) : (
              <button className="btn-yellow" onClick={handleLoginClick}>
                Login
              </button>
            )}
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="hero">
        <div className="shape shape-1"></div>
        <div className="shape shape-2"></div>

        <div className="container">
          <div className="hero-content">
            <div className="hero-text">
              <div className="pill">
                <span>🔐</span>
                <span>Secure Employee Portal</span>
              </div>

              <h1>
                Welcome to <span className="brand-yellow">Syberkey Pvt Ltd</span> Employee Portal
              </h1>

              <p className="lead">
                Access your company resources securely using <span className="brand-cyan">SyberKey SSO</span> authentication.
                One-time enrollment, seamless approvals, and enterprise-grade security.
              </p>

              <div className="hero-badges">
                <span className="badge-soft">
                  🔒 No static credentials
                </span>
                <span className="badge-soft">
                  ⚡ Single Sign-On
                </span>
                <span className="badge-soft">
                  📱 Mobile approvals
                </span>
              </div>

              <div className="hero-actions">
                {!isAuthenticated ? (
                  <button className="btn-yellow btn-large" onClick={handleLoginClick}>
                    Get Started
                  </button>
                ) : (
                  <button className="btn-yellow btn-large" onClick={handleDashboardClick}>
                    Go to Dashboard
                  </button>
                )}
              </div>
            </div>

            <div className="hero-card">
              <div className="card-syber">
                <div className="card-header">
                  <h2>Quick Access</h2>
                  <span className="badge-soft">Employee Portal</span>
                </div>

                <div className="card-content">
                  {!isAuthenticated ? (
                    <>
                      <button className="btn-yellow btn-full" onClick={handleLoginClick}>
                        Login with SyberKey SSO
                      </button>
                      <div className="divider">or</div>
                      <button className="btn-outline-cyan btn-full" onClick={handleLoginClick}>
                        Standard Login
                      </button>
                    </>
                  ) : (
                    <button className="btn-yellow btn-full" onClick={handleDashboardClick}>
                      Access Dashboard
                    </button>
                  )}

                  <div className="card-features">
                    <div className="feature-item">
                      <div className="icon-bubble">
                        <span>🆔</span>
                      </div>
                      <div>
                        <div className="feature-title">True "One ID"</div>
                        <div className="feature-desc">Identity Authenticator Platform</div>
                      </div>
                    </div>
                    <div className="feature-item">
                      <div className="icon-bubble yellow">
                        <span>🔑</span>
                      </div>
                      <div>
                        <div className="feature-title">True "One Password"</div>
                        <div className="feature-desc">No OTPs, no shared secrets</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Feature Cards */}
          <div className="feature-cards">
            <div className="card-syber">
              <div className="icon-bubble yellow">
                <span>🛡️</span>
              </div>
              <h3>Eliminate threat vectors</h3>
              <p>Remove the primitives that fuel modern attacks: static credentials.</p>
            </div>
            <div className="card-syber">
              <div className="icon-bubble">
                <span>👆</span>
              </div>
              <h3>Remote biometrics</h3>
              <p>Approvals happen on the user's phone with secured verification.</p>
            </div>
            <div className="card-syber">
              <div className="icon-bubble">
                <span>🏢</span>
              </div>
              <h3>Enterprise-ready</h3>
              <p>Banking, healthcare, enterprise security, payments, and access.</p>
            </div>
            <div className="card-syber">
              <div className="icon-bubble yellow">
                <span>🔗</span>
              </div>
              <h3>Hybrid models</h3>
              <p>Identity document/number flows can be supported (when required).</p>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer>
        <div className="container">
          <div className="footer-content">
            <div className="footer-brand">
              <div className="footer-brand-name">
                <span className="brand-white">Syber</span>
                <span className="brand-yellow">Key</span>
              </div>
              <p>Securing the future, one identity at a time.</p>
            </div>
            <div className="footer-copyright">
              <p>© 2024 Syberkey Pvt Ltd. All rights reserved.</p>
              <p>
                Powered by <SyberKeyInlineMark />
              </p>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default HomePage;
